const EXCEL_TEXT_CELL_STYLE = "mso-number-format:'\\@';";

const toRotationKey = (value) => String(value ?? "").trim();

/**
 * 轮档号比较必须满足全序：纯数字按数值、其余按文本、空值最后，数字与文本两类之间方向固定。
 * 若对部分输入走数值、对部分输入走文本，比较会变得不传递，排序结果依赖输入顺序。
 */
const compareRotationKeys = (keyA, keyB) => {
    if (keyA === keyB) return 0;
    if (!keyA) return 1;
    if (!keyB) return -1;
    const numA = Number(keyA);
    const numB = Number(keyB);
    const aIsNumeric = Number.isFinite(numA);
    const bIsNumeric = Number.isFinite(numB);
    if (aIsNumeric && bIsNumeric) return numA - numB;
    if (aIsNumeric !== bIsNumeric) return aIsNumeric ? -1 : 1;
    return keyA.localeCompare(keyB);
};

/**
 * 按 aircraft_reg → rotation_no → 离港时间排序，并在不同飞机或不同轮档之间插入 null 分隔标记
 * 排序后数组中含 flight 对象和 null（分隔行），便于复制时自动插入空白行
 *
 * 分组边界：没有飞机号的排在有飞机号的后面；同一架飞机内，有轮档号的排在没有轮档号的前面；
 * 轮档号按数字大小排（避免 "10" 排到 "2" 前面）。排序与插空行共用同一份轮档号键，同一轮档不会被空行拆开
 */
export const sortFlightsWithRotationSeparators = (flights) => {
    if (!flights || flights.length <= 1) return flights ? [...flights] : [];

    const sorted = [...flights].sort((a, b) => {
        const regA = a.aircraft_reg || "";
        const regB = b.aircraft_reg || "";
        if (regA !== regB) {
            if (!regA) return 1;
            if (!regB) return -1;
            return regA.localeCompare(regB);
        }

        const rotationCmp = compareRotationKeys(toRotationKey(a.rotation_no), toRotationKey(b.rotation_no));
        if (rotationCmp !== 0) return rotationCmp;

        const timeA = a.etd || a.std || a.flight_date;
        const timeB = b.etd || b.std || b.flight_date;
        if (timeA && timeB) {
            return new Date(timeA).getTime() - new Date(timeB).getTime();
        }
        return 0;
    });

    const result = [];
    let prevRotation = undefined;
    let prevAircraft = undefined;

    for (const flight of sorted) {
        const curRotation = toRotationKey(flight.rotation_no);
        const curAircraft = flight.aircraft_reg || "";

        if (prevRotation !== undefined && (prevAircraft !== curAircraft || prevRotation !== curRotation)) {
            result.push(null);
        }

        result.push(flight);
        prevRotation = curRotation;
        prevAircraft = curAircraft;
    }

    return result;
};

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

export const buildTabSeparatedTable = (rows) => rows.map((row) => row.map((cell) => cell ?? "").join("\t")).join("\n");

export const buildExcelTextTableHtml = (rows) => {
    const tableRows = rows
        .map((row) => `<tr>${row.map((cell) => `<td style="${EXCEL_TEXT_CELL_STYLE}">${escapeHtml(cell)}</td>`).join("")}</tr>`)
        .join("");

    return `<html><body><table>${tableRows}</table></body></html>`;
};

export const writeExcelFriendlyTableToClipboard = async (rows, html = buildExcelTextTableHtml(rows)) => {
    const tableText = buildTabSeparatedTable(rows);

    if (typeof navigator !== "undefined" && navigator.clipboard?.write && typeof window !== "undefined" && window.ClipboardItem) {
        try {
            const clipboardItem = new window.ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([tableText], { type: "text/plain" }),
            });

            await navigator.clipboard.write([clipboardItem]);
            return;
        } catch (error) {
            console.warn("HTML clipboard write failed, falling back to plain text:", error);
        }
    }

    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(tableText);
        return;
    }

    throw new Error("Clipboard API is not available");
};
